import React, { useState, useEffect } from 'react'
import { withRouter } from 'react-router-dom'
import NewHeader from '../../components/NewHeader'
import WelcomeSection from './components/WelcomeSection'
import CompetitionFilter from './components/CompetitionFilter'
import StatsCards from './components/StatsCards'
import BadgesSection from './components/BadgesSection'
import ProgressSection from './components/ProgressSection'
import LeaderboardTable from './components/LeaderboardTable'
import LeaderboardSettings from './components/LeaderboardSettings/LeaderboardSettings'
import api from '../../services/api'
import './Leaderboard.scss'
import { useSelector } from 'react-redux'
import { trophy } from '../../components/NavNewDesign/images'
import { useTranslation } from 'react-i18next'

const Leaderboard = props => {
  const { history, location } = props
  const user = useSelector(state => state.user)
  const userId = user && user.info && user.info.id
  const [activeTab, setActiveTab] = useState('team')
  const [competitions, setCompetitions] = useState([])
  const [selectedCompetition, setSelectedCompetition] = useState({
    value: '',
    label: ''
  })
  const [fetching, setFetching] = useState(false)
  const [fetchingUser, setFetchingUser] = useState(false)
  const [search, setSearch] = useState('')
  const [showLoading, setShowLoading] = useState(false)
  const [error, setError] = useState(null)
  const [errorType, setErrorType] = useState({
    competition: false,
    dashboard: false
  })

  const [leaderboardData, setLeaderboardData] = useState([])
  // Custom route logic for settings/team and settings/global
  const isSettingsTeam = location.pathname.endsWith('/settings/team')
  const isSettingsGlobal = location.pathname.endsWith('/settings/global')
  const isSettingsHistory = location.pathname.endsWith('/history')

  const { t } = useTranslation()

  useEffect(() => {
    if (activeTab === 'team') {
      fetchCompetitions()
    } else if (activeTab === 'global') {
      fetchGlobalCompetitions()
    }
  }, [search, activeTab])

  useEffect(() => {
    if (selectedCompetition.value) {
      getCompetitionUser()
    }
  }, [selectedCompetition.value])

  // Update showLoading based on loading flags
  useEffect(() => {
    if (fetching || fetchingUser) {
      setShowLoading(true)
      setError(null) // Clear any previous errors when loading starts
    } else {
      setShowLoading(false)
    }
  }, [fetching, fetchingUser])

  const fetchCompetitions = async () => {
    try {
      setFetching(true)
      setError(null)
      setErrorType({
        ...errorType,
        competition: false
      })
      const response = await api.leaderboard.getCompetitions({
        mode: 'training',
        offset: 0,
        limit: 25,
        search: search
      })

      const data = response.data
      const newCompetitions = (data && data.data) || []
      setCompetitions(newCompetitions)

      if (newCompetitions.length > 0) {
        // Check if the currently selected competition still exists in the new results
        const currentCompetitionExists = newCompetitions.find(
          comp => comp.id === selectedCompetition.value
        )

        if (currentCompetitionExists) {
          // Keep the current selection if it still exists - no need to trigger loading
          // Only update if the label might have changed (e.g., competition title was updated)
          if (currentCompetitionExists.title !== selectedCompetition.label) {
            setSelectedCompetition({
              value: currentCompetitionExists.id,
              label: currentCompetitionExists.title
            })
          }
          // Don't trigger getCompetitionUser since data is already there
        } else {
          // Set to first competition only if current selection doesn't exist
          setSelectedCompetition({
            value: newCompetitions[0].id,
            label: newCompetitions[0].title
          })
        }
      }
    } catch (error) {
      setError(`${error.message}. Please try again.`)
      setErrorType({
        ...errorType,
        competition: true
      })
    } finally {
      setFetching(false)
    }
  }
  const fetchGlobalCompetitions = async () => {
    try {
      setFetching(true)
      setError(null)
      setErrorType({
        ...errorType,
        competition: false
      })
      const response = await api.leaderboard.getGlobalCompetitions()
      setLeaderboardData(response.data || [])
    } catch (error) {
      setError(`${error.message}. Please try again.`)
      setErrorType({
        ...errorType,
        competition: true
      })
    } finally {
      setFetching(false)
    }
  }
  const navigateTo = () => {
    history.goBack()
    if (activeTab === 'team') {
      fetchCompetitions()
    } else if (activeTab === 'global') {
      fetchGlobalCompetitions()
    }
  }
  const getCompetitionUser = async () => {
    try {
      setFetchingUser(true)
      setError(null)
      setErrorType({
        ...errorType,
        dashboard: false
      })
      const response = await api.leaderboard.getCompetitionUser({
        id: selectedCompetition.value,
        userId: userId,
        date: '',
        from: '',
        to: ''
      })

      setLeaderboardData(response.data || [])
    } catch (error) {
      setError(`${error.message}. Please try again.`)
      setErrorType({
        ...errorType,
        dashboard: true
      })
    } finally {
      setFetchingUser(false)
    }
  }
  if (isSettingsTeam || isSettingsGlobal || isSettingsHistory) {
    return (
      <LeaderboardSettings
        goBack={() => navigateTo()}
        type={isSettingsTeam ? 'team' : isSettingsGlobal ? 'global' : 'history'}
        selectedCompetition={selectedCompetition}
        userId={userId}
      />
    )
  }

  return (
    <div className="leaderboard-main">
      <div className="leaderboard-container">
        <NewHeader />
        <div className="leaderboard-content">
          <WelcomeSection
            activeTab={activeTab}
            setActiveTab={tab => {
              setLeaderboardData([])
              setCompetitions([])
              setSelectedCompetition({ label: '', value: '' })
              setActiveTab(tab)
            }}
            onSettingsClick={type => {
              try {
                if (type === 'competitions') {
                  history.push('/leaderboard/settings/team')
                } else {
                  history.push('/leaderboard/settings/global')
                }
              } catch (error) {
                console.error('Navigation error:', error)
              }
            }}
          >
            {activeTab === 'team' && (search || competitions.length > 0) && (
              <CompetitionFilter
                competitions={competitions}
                selectedCompetition={selectedCompetition}
                onCompetitionChange={data => {
                  if (data.value !== selectedCompetition.value) {
                    setSelectedCompetition(data)
                  }
                }}
                onSearch={setSearch}
                fetching={fetching}
              />
            )}
          </WelcomeSection>

          {/* Error Container */}
          {error && (
            <div className="leaderboard-error-section">
              <div className="error-container">
                <div className="error-icon">⚠️</div>
                <div className="error-content">
                  <h3>{t('general:something_went_wrong')}</h3>
                  <p>{error}</p>
                  <button
                    className="error-retry-btn"
                    onClick={() => {
                      if (errorType.competition) {
                        fetchCompetitions()
                      } else if (errorType.dashboard) {
                        getCompetitionUser()
                      }
                    }}
                  >
                    {t('v4:try_again')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Loading Section with Custom Skeleton */}
          {showLoading && (
            <div className="leaderboard-skeleton-section">
              {/* Stats Cards Skeleton */}
              <div className="stats-cards-skeleton">
                <div className="stats-row">
                  <div className="stat-card-skeleton">
                    <div className="stat-content">
                      <div className="stats-info">
                        <div className="skeleton-number skeleton-pulse"></div>
                        <div className="skeleton-label skeleton-pulse"></div>
                        <div className="stat-change">
                          <div className="skeleton-change skeleton-pulse"></div>
                          <div className="skeleton-change-text skeleton-pulse"></div>
                        </div>
                      </div>
                      <div className="skeleton-icon skeleton-pulse"></div>
                    </div>
                  </div>
                  <div className="stat-card-skeleton">
                    <div className="stat-content">
                      <div className="stats-info">
                        <div className="skeleton-number skeleton-pulse"></div>
                        <div className="skeleton-label skeleton-pulse"></div>
                        <div className="stat-change">
                          <div className="skeleton-change skeleton-pulse"></div>
                          <div className="skeleton-change-text skeleton-pulse"></div>
                        </div>
                      </div>
                      <div className="skeleton-icon skeleton-pulse"></div>
                    </div>
                  </div>
                  <div className="stat-card-skeleton">
                    <div className="stat-content">
                      <div className="stats-info">
                        <div className="skeleton-number skeleton-pulse"></div>
                        <div className="skeleton-label skeleton-pulse"></div>
                        <div className="stat-change">
                          <div className="skeleton-change skeleton-pulse"></div>
                          <div className="skeleton-change-text skeleton-pulse"></div>
                        </div>
                      </div>
                      <div className="skeleton-icon skeleton-pulse"></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Badges and Progress Skeleton */}
              <div className="badges-progress-skeleton">
                <div className="badges-col-skeleton">
                  <div className="section-card-skeleton">
                    <div className="badges-header">
                      <div className="skeleton-title skeleton-pulse"></div>
                      <div className="skeleton-icon skeleton-pulse"></div>
                    </div>
                    <div className="badges-content">
                      <div className="skeleton-badges-grid">
                        {[1, 2, 3, 4, 5, 6].map(index => (
                          <div
                            key={index}
                            className="skeleton-badge skeleton-pulse"
                          ></div>
                        ))}
                      </div>
                      <div className="skeleton-badge-text skeleton-pulse"></div>
                    </div>
                  </div>
                </div>
                <div className="progress-col-skeleton">
                  <div className="section-card-skeleton">
                    <div className="progress-header">
                      <div className="skeleton-title skeleton-pulse"></div>
                      <div className="skeleton-icon skeleton-pulse"></div>
                    </div>
                    <div className="progress-content">
                      <div className="skeleton-progress-bar-container">
                        <div className="skeleton-progress-bar skeleton-pulse"></div>
                        <div className="skeleton-progress-percentage skeleton-pulse"></div>
                      </div>
                      <div className="skeleton-progress-text skeleton-pulse"></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Leaderboard Table Skeleton */}
              <div className="leaderboard-table-skeleton">
                <div className="table-card-skeleton">
                  <div className="skeleton-table-title skeleton-pulse"></div>
                  <div className="skeleton-controls">
                    <div className="skeleton-filter-tabs">
                      <div className="skeleton-filter skeleton-pulse"></div>
                      <div className="skeleton-filter skeleton-pulse"></div>
                      <div className="skeleton-filter skeleton-pulse"></div>
                      <div className="skeleton-filter skeleton-pulse"></div>
                      <div className="skeleton-filter skeleton-pulse"></div>
                    </div>
                    <div className="skeleton-search-controls">
                      <div className="skeleton-history-btn skeleton-pulse"></div>
                      <div className="skeleton-search-input skeleton-pulse"></div>
                    </div>
                  </div>
                  <div className="table-content-skeleton">
                    {[1, 2, 3, 4, 5].map(index => (
                      <div key={index} className="table-row-skeleton">
                        <div className="skeleton-rank skeleton-pulse"></div>
                        <div className="user-cell-skeleton">
                          <div className="skeleton-avatar skeleton-pulse"></div>
                          <div className="skeleton-username skeleton-pulse"></div>
                        </div>
                        <div className="skeleton-points skeleton-pulse"></div>
                        <div className="skeleton-level skeleton-pulse"></div>
                        <div className="skeleton-badges skeleton-pulse"></div>
                        <div className="skeleton-progress skeleton-pulse"></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Empty State - Show when no competitions */}
          {!showLoading &&
            !error &&
            competitions.length === 0 &&
            activeTab === 'team' && (
              <div className="no-competitions-message">
                <div className="no-competitions-icon">
                  <img
                    src={trophy}
                    alt={search ? 'No Search Results' : 'No Competitions'}
                  />
                </div>
                <div className="no-competitions-title">
                  {search
                    ? `No Competitions Available for "${search}"`
                    : t('v4:no_competitions_available')}
                </div>
                <div className="no-competitions-desc">
                  {search
                    ? t('v4:no_competitions_matching_search')
                    : t('v4:no_competitions_general')}
                </div>
              </div>
            )}

          {/* Content Sections - Only show when not loading, no error, and competitions exist */}
          {!showLoading &&
            !error &&
            ((activeTab === 'team' && competitions.length > 0) ||
              (activeTab === 'global' && leaderboardData)) && (
              <>
                <StatsCards
                  boardType={activeTab}
                  leaderboardData={leaderboardData}
                />
                {activeTab === 'team' ? (
                  <div className="badges-progress-row">
                    <div className="progress-col">
                      <ProgressSection leaderboardData={leaderboardData} />
                    </div>
                    <div className="badges-col">
                      <BadgesSection leaderboardData={leaderboardData} />
                    </div>
                  </div>
                ) : (
                  <div
                    className="global-leaderboard-row"
                    style={{
                      display: 'flex',
                      gap: 24,
                      marginTop: 24,
                      marginBottom: 24
                    }}
                  >
                    <div style={{ flex: 2 }}>
                      <ProgressSection leaderboardData={leaderboardData} />
                    </div>
                    <div style={{ flex: 3 }}>
                      <BadgesSection leaderboardData={leaderboardData} />
                    </div>
                  </div>
                )}
                <div className="leaderboard-table-row">
                  <LeaderboardTable
                    leaderboardData={leaderboardData}
                    loading={fetchingUser}
                    selectedCompetition={selectedCompetition}
                    userId={userId}
                    activeTab={activeTab}
                  />
                </div>
              </>
            )}
        </div>
      </div>
    </div>
  )
}

export default withRouter(Leaderboard)
